/**
 * Pure helpers for AI Coach training-plan workflow UI state.
 * Backend coaching context + materialisation refs are the source of truth.
 */

export const WORKFLOW_ACTION = {
  IDLE: "IDLE",
  GENERATING: "GENERATING",
  CONFIRMING: "CONFIRMING",
  MATERIALISING: "MATERIALISING",
  ACTIVATING: "ACTIVATING",
  REPLACING: "REPLACING",
};

export const createClientRequestId = (prefix = "coach-plan") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const isActiveTrainingBlock = (block) =>
  Boolean(
    block?.block_id
    && block?.current_block_status === "CURRENT"
    && block?.resolution_status !== "MISSING",
  );

/**
 * Derive the athlete-visible plan state from authoritative context + workflow.
 */
export const derivePlanUiState = ({
  workflow = null,
  activeBlock = null,
  action = WORKFLOW_ACTION.IDLE,
}) => {
  const hasActive = isActiveTrainingBlock(activeBlock);
  const materialisationRef = workflow?.materialisationRef || null;
  const confirmationRef = workflow?.confirmationRef || null;
  const draftRef = workflow?.draftRef || null;

  if (action === WORKFLOW_ACTION.REPLACING) {
    return {
      state: "replacing",
      label: "Replacing active plan",
      hasActive,
      materialisationRef,
    };
  }
  if (action === WORKFLOW_ACTION.ACTIVATING) {
    return {
      state: "activating",
      label: "Activating plan",
      hasActive,
      materialisationRef,
    };
  }

  if (hasActive && !materialisationRef && !confirmationRef && !draftRef) {
    return { state: "active", label: "Active plan", hasActive };
  }

  if (materialisationRef) {
    if (hasActive) {
      return {
        state: "pending_replacement",
        label: "Ready to replace current plan",
        hasActive,
        materialisationRef,
      };
    }
    return {
      state: "materialised",
      label: "Plan confirmed – not active",
      hasActive,
      materialisationRef,
    };
  }

  if (confirmationRef) {
    return {
      state: "confirmed",
      label: "Plan confirmed – not active",
      hasActive,
      confirmationRef,
    };
  }

  if (draftRef) {
    return {
      state: "draft",
      label: "Generated plan draft",
      hasActive,
      draftRef,
    };
  }

  if (hasActive) {
    return { state: "active", label: "Active plan", hasActive };
  }

  return { state: "none", label: "No plan", hasActive: false };
};

export const primarySportLabel = ({ athlete, todaySessions = [] }) => {
  if (athlete?.is_multisport) return "Multisport";
  if (athlete?.primary_sport) return athlete.primary_sport;
  const sessionSport = todaySessions.find((s) => s?.sport)?.sport;
  return sessionSport || null;
};

export const primaryGoalLabel = (goalContext) => {
  if (!goalContext) return null;
  if (typeof goalContext.original_text === "string" && goalContext.original_text.trim()) {
    return goalContext.original_text.trim();
  }
  const goal = goalContext.primary_goal;
  if (!goal) return null;
  return goal.title || goal.name || goal.goal_type || goal.objective || null;
};
